/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

/* eslint-env browser */

import isNil from 'lodash/isNil';
import isString from 'lodash/isString';

/**
 * Utility pro přenosy souborů po síti
 */
class FileTransferUtil {
  /**
   * @param {HeadersUtil} headersUtil
   * @param {Logger} logger
   * @param {RequestUtil} requestUtil
   */
  constructor(headersUtil, logger, requestUtil) {
    /**
     * Výchozí hlavičky pro HTTP požadavky
     *
     * Tyto hlavičky se používají, aby se zabránilo vrácení odpovědi z cache.
     * Velké soubory nejsou příliš vhodné k ukládání do cache
     * a nelze zaručit přítomnost cache na všech platformách.
     *
     * Hlavičky se vkládají automaticky, protože by bez nich stahování souborů
     * nefungovalo správně.
     * Např. by mohlo dojít k tomu, že multiplatformní HEAD dotazy by
     * se cachovaly, ale platformově specifické stahování binárních souborů
     * by cachováno nebylo.
     * Obdobným způsobem přidává vybrané hlavičky i Fetch API.
     * Uživatel může hlavičky také nastavit, ale pouze na tyto výchozí hodnoty.
     * @private
     * @type {Object.<string>}
     */
    this.defaultHeaders = {
      'Cache-Control': 'no-store',
      Pragma: 'no-cache'
    };
    this.headersUtil = headersUtil;
    this.logger = logger;
    /**
     * Seznam omezení na `Request` a `RequestInit`
     *
     * Obsahuje mapu vlastností u kterých jsou vyžadované konkrétní hodnoty.
     * Klíčem v mapě je název položky v `Request` a `RequestInit`.
     * Některé položky mohou ve staších verzích API chybět,
     * proto nejsou označené jako `required`.
     *
     * Položka `body` se nevaliduje, protože je to složitější.
     * V `RequestInit` musí být `undefined`,
     * ale výsledkem `Request.text()` je `""`.
     * Navíc tyto hodnoty nelze zaměnit.
     * Validaci `body` obstarává fetch knihovna,
     * která nedovoluje nastavení těla u metod GET a HEAD.
     *
     * Položka `signal` se nevaliduje, i když na ni také existuje omezení.
     * Validace totiž není s aktuálním Fetch API možné.
     * V `RequestInit` může být undefined, ale v `Request` už ne.
     * Navíc z `Request#signal` nelze poznat, že je neaktivní.
     * Jediným řešením by asi byla vlastní implementace `Request`.
     * Zatím se tedy ignoruje.
     *
     * Hodnotu položky `window` nelze z již vytvořeného Request vyčíst.
     * Ale vzhledem k tomu, že může být pouze `undefined` nebo `null`,
     * tak by měla být její hodnota po nastavení výchozí na `null` zaručena.
     * @private
     * @type {Object.<FileTransferUtil~RequestConstraint>}
     */
    this.requestConstraints = {
      cache: { value: 'default' },
      credentials: { required: true, value: 'omit' },
      integrity: { value: '' },
      keepalive: { value: false },
      method: { required: true, value: 'GET' },
      mode: { required: true, value: 'cors' },
      redirect: { value: 'follow' },
      referrer: { required: true, value: '' },
      referrerPolicy: { value: 'no-referrer' },
      window: { value: null }
    };
    this.requestUtil = requestUtil;
  }

  /**
   * Vytvoří GET požadavek pro stažení souboru.
   * Na požadavku se aplikují specifické výchozí hodnoty a pak se zkontroluje,
   * že jsou všechna nastavení podporovaná.
   * @param {Request|string} input
   * @param {RequestInit} [init]
   * @returns {Request}
   */
  createGetRequest(input, init) {
    const request = this.initRequest(input, init);
    const { message, valid } = this.validateRequest(request);
    if (!valid) {
      throw new TypeError(`Failed to download: ${message}`);
    }
    return request;
  }

  /**
   * Vytvoří HEAD požadavek pro stažení souboru.
   * HEAD požadavek se vytváří zkopírováním GET požadavku a nastavením
   * HTTP metody na HEAD.
   * HEAD požadavky se odesílají na platformách,
   * které nepodporují zjištění hlaviček ze staženého souboru.
   * @param {Request} getRequest
   * @returns {Promise.<Request>}
   */
  createHeadRequest(getRequest) {
    return new Promise((resolve) =>
      resolve(
        this.requestUtil.toInit(getRequest).then((getInit) => {
          const headInit = { ...getInit, method: 'HEAD' };
          return new Request(getRequest.url, headInit);
        })
      )
    );
  }

  /**
   * Vytvoří se odpověď 304 `Not Modified`, pro případ, že by server odpověděl
   * statusem v rozsahu 200 - 299, ale obsah hlaviček by tomu neodpovídal.
   * @param {Headers} okResponseHeaders
   * @private
   * @returns {Response}
   */
  createNotModifiedResponse(okResponseHeaders) {
    return new Response(undefined, {
      status: 304,
      headers: this.headersUtil.toObject(okResponseHeaders)
    });
  }

  /**
   * Odešle HEAD požadavek před stažení souboru.
   * Ošetří se možné chyby serveru při zpracování hlaviček `If-None-Match`
   * a `If-Modified-Since` tím, že se provede jejich kontrola.
   * Dále se ošetří případné selhání požadavku pro případ,
   * že by HEAD požadavek neuspěl, např. z důvodu CORS omezení.
   * HEAD požadavky se odesílají na platformách,
   * které nepodporují zjištění hlaviček ze staženého souboru.
   * @param {Request} headRequest
   * @returns {Promise.<Response>}
   */
  fetchHeadBeforeDownload(headRequest) {
    return new Promise((resolve) =>
      resolve(
        fetch(headRequest).then(
          (response) => {
            // U statusů mimo rozsah 200 - 299 se nemusí kontrola hlaviček dělat.
            if (!response.ok) {
              return response;
            }
            // Kontrola hlavičky `If-None-Match`
            const requestHeaders = headRequest.headers;
            const responseHeaders = response.headers;
            const ifNoneMatch = requestHeaders.get('If-None-Match');
            const etag = responseHeaders.get('ETag');
            if (!isNil(ifNoneMatch) && !isNil(etag)) {
              // TODO If-None-Match může být *, ale mělo by se to týkat jen PUT
              if (ifNoneMatch.includes(etag)) {
                return this.createNotModifiedResponse(responseHeaders);
              }
              return response;
            }
            // Kontrola hlavičky `If-Modified-Since`
            const ifModifiedSince = requestHeaders.get('If-Modified-Since');
            const lastModified = responseHeaders.get('Last-Modified');
            if (!isNil(ifModifiedSince) && !isNil(lastModified)) {
              const ifModifiedSinceTime = new Date(ifModifiedSince).getTime();
              const lastModifiedTime = new Date(lastModified).getTime();
              // Pokud je v požadavku hlavička If-Modified-Since
              // a v odpovědi je hlavička Last-Modified,
              // tak se hodnoty těchto hlaviček porovnají.
              // Return 200 status, only if it has been last modified after
              // the modified since date
              if (
                !isNaN(ifModifiedSinceTime) &&
                !isNaN(lastModifiedTime) &&
                ifModifiedSinceTime >= lastModifiedTime
              ) {
                return this.createNotModifiedResponse(responseHeaders);
              }
              return response;
            }
            return response;
          },
          (error) => this.handleFetchHeadError(error)
        )
      )
    );
  }

  /**
   * Odešle HEAD požadavek po stažení souboru.
   * Ověřuje, že se hodnoty hlaviček `ETag` a `Last-Modified` nezměnili
   * od prvního HEAD požadavku, jinak by nebylo jasné,
   * které z nich patří ke staženému souboru.
   * HEAD požadavky se odesílají na platformách,
   * které nepodporují zjištění hlaviček ze staženého souboru.
   * @param {Response} firstResponse
   * @param {Request} headRequest
   * @param {string} url
   * @returns {Promise.<Response>}
   */
  fetchHeadAfterDownload(firstResponse, headRequest, url) {
    return new Promise((resolve) => {
      if (!firstResponse.ok) {
        return resolve(firstResponse);
      }
      const firstHeaders = firstResponse.headers;
      const firstEtag = firstHeaders.get('ETag');
      const hasEtag = !isNil(firstEtag);
      const firstLastModified = firstHeaders.get('Last-Modified');
      const hasLastModified = !isNil(firstLastModified);
      if (!hasEtag && !hasLastModified) {
        return resolve(firstResponse);
      }
      return resolve(
        // Z head požadavku se převezme init konfigurace.
        // URL se použije finální po přesměrování, které provedl první HEAD.
        this.requestUtil
          .toInit(headRequest)
          .then((headInit) => fetch(url, headInit))
          .then(
            (secondResponse) => {
              if (firstResponse.status !== secondResponse.status) {
                return new Response();
              }
              const secondHeaders = secondResponse.headers;
              const secondEtag = secondHeaders.get('ETag');
              const secondLastModified = secondHeaders.get('Last-Modified');
              if (
                (hasEtag && firstEtag !== secondEtag) ||
                (hasLastModified && firstLastModified !== secondLastModified)
              ) {
                return new Response();
              }
              return firstResponse;
            },
            (error) => this.handleFetchHeadError(error)
          )
      );
    });
  }

  /**
   * Zjistí URL, která se má použít pro stažení souboru a druhý HEAD dotaz.
   * Preferuju se použití finální URL po všech přesměrováních.
   * To by mělo minimalizovat odlišnosti v přesměrování na různých platformách.
   * Také by to mělo minimalizovat možnost,
   * že se přesměrování mezi jednotlivými požadavky změní.
   * @param {Response} firstResponse
   * @param {Request} getRequest
   * @returns {Promise.<Response>}
   */
  getUrl(firstResponse, getRequest) {
    // Použití operátoru || je z důvodu, že zde může být řetězec
    // a teoreticky i null z XMLHttpRequest, který používá polyfill.
    // URL po přesměrování se tedy použije pouze, pokud je to neprázdný řetězec.
    return firstResponse.url || getRequest.url;
  }

  /**
   * Ošetří selhání při provádění HEAD dotazů.
   * Selhání se ignoruje, protože nelze zaručit 100% podporu pro HEAD dotazy,
   * především kvůli možnosti aplikace CORS omezení.
   * Také nelze rozeznat selhání kvůli CORS a kvůli jinému důvodu.
   * Výsledkem je v tom případě prázdná odpověď se statusem 200,
   * což je stejná reakce jako při původním stahování bez HEAD požadavků.
   * @param {Error} error
   * @private
   * @returns {Response}
   */
  handleFetchHeadError(error) {
    this.logger.warn(error);
    return new Response();
  }

  /**
   * Vytvoří požadavek ze vstupů ve formátu pro fetch metodu.
   * Na požadavku se aplikují specifické výchozí hodnoty.
   * @param {Request|string} input
   * @param {RequestInit} [init]
   * @private
   * @returns {Request}
   */
  initRequest(input, init) {
    if (isString(input)) {
      const initObject = init ?? {};
      const initDefaults = Object.fromEntries(
        Object.entries(this.requestConstraints).map(([key, { value }]) => [
          key,
          value
        ])
      );
      initDefaults.headers = this.defaultHeaders;
      const requestInit = this.requestUtil.mergeInit(initDefaults, initObject);
      return new Request(input, requestInit);
    }
    if (!isNil(init)) {
      // TODO Podporovat Request + init dle specifikace
      throw new Error('Not implemented.');
    }
    return input;
  }

  /**
   * Zkontroluje, jestli jsou všechny hlavičky podporovány.
   * @param {Headers} headers
   * @private
   * @returns {FileTransferUtil~ValidationResult}
   */
  validateHeaders(headers) {
    const entries = Object.entries(this.defaultHeaders);
    const foundEntry = entries.find(
      ([key, value]) => headers.get(key) !== value
    );
    if (!isNil(foundEntry)) {
      const [key, value] = foundEntry;
      return {
        message: `Header ${key} is "${headers.get(
          key
        )}" instead of "${value}".`,
        valid: false
      };
    }
    return { valid: true };
  }

  /**
   * Zkontroluje, jestli jsou všechna nastavení požadavku podporována.
   * @param {Request} request
   * @private
   * @returns {FileTransferUtil~ValidationResult}
   */
  validateRequest(request) {
    const entries = Object.entries(this.requestConstraints);
    const foundEntry = entries.find(
      ([key, { required, value }]) =>
        !(request[key] === value || (!required && request[key] === undefined))
    );
    if (!isNil(foundEntry)) {
      const [key, values] = foundEntry;
      return {
        message: `Request ${key} is "${request[key]}" instead of "${values[0]}".`,
        valid: false
      };
    }
    return this.validateHeaders(request.headers);
  }
}

/**
 * Omezení na `Request` a `RequestInit`
 * @typedef {Object} FileTransferUtil~RequestConstraint
 * @property {boolean} required Příznak, jestli musí mít `Request` tuto hodnotu
 * nastavenou.
 * @property {*} value Výchozí a zároveň jediná povolená hodnota pro danou
 * položku `Request` nebo `RequestInit`.
 */

/**
 * Výsledek validace `Headers` a `Request`
 * @typedef {Object} FileTransferUtil~ValidationResult
 * @property {string} [message] Chybové hlášení, které je vyplněné,
 * pokud objekt není validní.
 * @property {boolean} valid Příznak, jestli je objekt validní.
 */

export default FileTransferUtil;
